import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo, useEffect } from 'react';
import { usePages, useDeletePage, useUpdatePage, useSiteSettings, useUpdateSiteSettings } from '@wordpuppi/shared';
import { cn } from '@/lib/utils';
import { useBaxterChanged } from '@/lib/baxterEffects';
import { useTurnHighlight } from '@/lib/useTurnHighlight';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { confirmDialog } from '@/lib/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Binoculars, GripVertical, Link2, Pencil, Plus, Trash2 } from 'lucide-react';
import type { NavLink, Page, UpdateSiteSettingsRequest } from '@wordpuppi/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MAX_NAV_LINKS, navLinksForOrder, validateNavLink, type NavLinkErrors } from '@/lib/navLinks';
import { openPagePreview } from '@/lib/pagePreview';
import { customTemplate } from '@/lib/designPage';

export const Route = createFileRoute('/sites/$siteSlug/pages/')({
  component: PagesPage,
});

// Menu item can be a page, the blog, or a custom URL link (AB#649)
type MenuItem = {
  id: string;
  type: 'page' | 'blog' | 'link';
  title: string;
  status: string;
  created_at: string;
  menu_order: number;
  page?: Page;
  link?: NavLink;
  /** Index into the site's `nav_links` array — only set for `type: 'link'`. */
  linkIndex?: number;
};

const BLOG_ID = '__blog__';
const LINK_PREFIX = '__link__';

// Sortable row component for pages
function SortablePageRow({
  item,
  siteSlug,
  onDelete,
  isDeleting,
  onToggleBlogStatus,
  isUpdatingBlog,
  onTogglePageStatus,
  onTogglePageNavigation,
  isUpdatingPage,
  onEditLink,
  changed,
}: {
  item: MenuItem;
  siteSlug: string;
  /** Baxter just wrote this page — flash it once. */
  changed?: boolean;
  onDelete: (id: string, title: string) => void;
  isDeleting: boolean;
  onEditLink?: (item: MenuItem) => void;
  onToggleBlogStatus?: (checked: boolean) => void;
  isUpdatingBlog?: boolean;
  onTogglePageNavigation?: (pageId: string, field: 'show_in_header' | 'show_in_footer', visible: boolean) => void;
  onTogglePageStatus?: (pageId: string, publish: boolean) => void;
  isUpdatingPage?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (item.type === 'blog') {
    const isPublished = item.status === 'published';
    return (
      <TableRow ref={setNodeRef} style={style} className={cn('bg-muted/30', changed && 'baxter-changed')} data-changed={changed ? 'true' : undefined}>
        <TableCell {...attributes} {...listeners} className="cursor-grab w-10">
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </TableCell>
        <TableCell>
          <Link to="/sites/$siteSlug/posts" params={{ siteSlug }} className="text-foreground font-medium hover:underline">
            Blog Posts
          </Link>
          <Badge variant="secondary" className="ml-2">system</Badge>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Switch
              checked={isPublished}
              onCheckedChange={onToggleBlogStatus}
              disabled={isUpdatingBlog}
            />
            <span className="text-sm text-muted-foreground">
              {isPublished ? 'In menu' : 'Hidden'}
            </span>
          </div>
        </TableCell>
        <TableCell className="text-muted-foreground">-</TableCell>
        <TableCell>
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" asChild>
              <Link to="/sites/$siteSlug/posts" params={{ siteSlug }}>View</Link>
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  if (item.type === 'link' && item.link) {
    return (
      <TableRow ref={setNodeRef} style={style} className={cn(changed && 'baxter-changed')} data-changed={changed ? 'true' : undefined}>
        <TableCell {...attributes} {...listeners} className="cursor-grab w-10">
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-foreground font-medium">{item.title}</span>
            <Badge variant="secondary">link</Badge>
          </div>
          <div className="text-xs text-muted-foreground truncate max-w-[420px]" title={item.link.url}>
            {item.link.url}
          </div>
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {item.link.new_tab ? 'New tab' : 'Same tab'}
        </TableCell>
        <TableCell className="text-muted-foreground">-</TableCell>
        <TableCell>
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground hover:text-foreground"
              aria-label="Edit link"
              title="Edit"
              onClick={() => onEditLink?.(item)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              aria-label="Delete link"
              title="Delete"
              onClick={() => onDelete(item.id, item.title)}
              disabled={isDeleting}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  const isPublished = item.status === 'published';

  return (
    <TableRow ref={setNodeRef} style={style} className={cn(changed && 'baxter-changed')} data-changed={changed ? 'true' : undefined}>
      <TableCell {...attributes} {...listeners} className="cursor-grab w-10">
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </TableCell>
      <TableCell>
        <Link
          to="/sites/$siteSlug/pages/$pageId"
          params={{ siteSlug, pageId: item.id }}
          className="text-foreground font-medium hover:underline"
        >
          {item.title || 'Untitled'}
        </Link>
        {customTemplate(item.page?.template) && (
          <Badge variant="secondary" className="ml-2 font-mono" title={`Template: ${item.page!.template}`}>
            {item.page!.template!.replace(/^pages\//, '').replace(/\.html$/, '')}
          </Badge>
        )}
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
          {([['show_in_header', 'Header'], ['show_in_footer', 'Footer']] as const).map(([field, label]) => (
            <label key={field} className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch checked={item.page?.[field] ?? true} disabled={isUpdatingPage} aria-label={`Show ${item.title || 'Untitled'} in ${label.toLowerCase()} navigation`}
                onCheckedChange={(visible) => onTogglePageNavigation?.(item.id, field, visible)} />
              {label}
            </label>
          ))}
        </div>
      </TableCell>
      <TableCell>
        {onTogglePageStatus ? (
          <div className="flex items-center gap-2">
            <Switch
              checked={isPublished}
              onCheckedChange={(checked) => onTogglePageStatus(item.id, checked)}
              disabled={isUpdatingPage}
              aria-label={isPublished ? 'Unpublish page' : 'Publish page'}
            />
            <span className="text-sm text-muted-foreground">
              {isPublished ? 'Published' : 'Draft'}
            </span>
          </div>
        ) : (
          <Badge variant={isPublished ? 'success' : 'secondary'}>
            {isPublished ? 'Published' : 'Draft'}
          </Badge>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {new Date(item.created_at).toLocaleDateString()}
      </TableCell>
      <TableCell>
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:text-foreground"
            aria-label="Preview page"
            title="Preview"
            onClick={() => openPagePreview(siteSlug, item.page ?? { id: item.id, title: item.title })}
          >
            <Binoculars className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:text-foreground"
            aria-label="Edit page"
            title="Edit"
            asChild
          >
            <Link to="/sites/$siteSlug/pages/$pageId" params={{ siteSlug, pageId: item.id }}>
              <Pencil className="h-4 w-4" />
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            aria-label="Delete page"
            title="Delete"
            onClick={() => onDelete(item.id, item.title)}
            disabled={isDeleting}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function PagesPage() {
  const { siteSlug } = Route.useParams();
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = usePages(siteSlug, { page, per_page: 100 });
  const { data: settingsData } = useSiteSettings(siteSlug);
  const deletePage = useDeletePage(siteSlug);
  const updatePage = useUpdatePage(siteSlug);
  const updateSettings = useUpdateSiteSettings(siteSlug);

  // Diff the RAW pages, not the merged menu items: `menuItems` is rebuilt by a
  // memo whose deps include settings, so it moves for reasons Baxter didn't cause.
  const flash = useTurnHighlight(data?.data?.map((row) => ({ id: row.id, title: row.title || 'Untitled', status: row.status, date: new Date(row.created_at).toLocaleDateString(), order: row.menu_order, header: row.show_in_header ?? true, footer: row.show_in_footer ?? true })), useBaxterChanged('pages'));

  const [localItems, setLocalItems] = useState<MenuItem[]>([]);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [editingLinkIndex, setEditingLinkIndex] = useState<number | null>(null);

  // Get blog_in_menu setting (defaults to true)
  const blogInMenu = settingsData?.data?.blog_in_menu ?? true;

  // AB#649 custom URL entries. Absent on every site created before AB#649, and
  // the server may omit it entirely — always read through `?? []`.
  const navLinks = settingsData?.data?.nav_links ?? [];

  // Build menu items from pages + blog
  const menuItems = useMemo(() => {
    if (!data?.data) return [];

    const blogOrder = settingsData?.data?.blog_menu_order ?? 0;

    // Create blog item
    const blogItem: MenuItem = {
      id: BLOG_ID,
      type: 'blog',
      title: 'Blog Posts',
      status: blogInMenu ? 'published' : 'draft',
      created_at: '',
      menu_order: blogOrder,
    };

    // Create page items
    const pageItems: MenuItem[] = data.data.map((p) => ({
      id: p.id,
      type: 'page' as const,
      title: p.title,
      status: p.status,
      created_at: p.created_at,
      menu_order: p.menu_order || 0,
      page: p,
    }));

    // Create custom link items (empty when the site has no nav_links)
    const linkItems: MenuItem[] = navLinks.map((l, i) => ({
      id: `${LINK_PREFIX}${i}`,
      type: 'link' as const,
      title: l.label,
      status: 'published',
      created_at: '',
      menu_order: l.menu_order ?? 0,
      link: l,
      linkIndex: i,
    }));

    // Combine and sort by menu_order
    return [blogItem, ...pageItems, ...linkItems].sort((a, b) => a.menu_order - b.menu_order);
  }, [data?.data, settingsData?.data?.blog_menu_order, settingsData?.data?.nav_links, blogInMenu]);

  // Sync local state with computed items
  useEffect(() => {
    if (menuItems.length > 0) {
      setLocalItems(menuItems);
    }
  }, [menuItems]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleToggleBlogStatus = async (checked: boolean) => {
    try {
      await updateSettings.mutateAsync({ blog_in_menu: checked });
      toast.success(checked ? 'Blog shown in menu' : 'Blog hidden from menu');
    } catch (err) {
      toast.error('Failed to update blog visibility');
    }
  };

  const handleTogglePageStatus = async (pageId: string, publish: boolean) => {
    try {
      await updatePage.mutateAsync({
        id: pageId,
        data: { status: publish ? 'published' : 'draft' },
      });
      toast.success(publish ? 'Page published' : 'Page unpublished');
    } catch {
      toast.error('Failed to update page status');
    }
  };

  const handleTogglePageNavigation = async (pageId: string, field: 'show_in_header' | 'show_in_footer', visible: boolean) => {
    try {
      await updatePage.mutateAsync({ id: pageId, data: { [field]: visible } });
      toast.success('Page navigation updated');
    } catch {
      toast.error('Failed to update page navigation');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localItems.findIndex((item) => item.id === active.id);
      const newIndex = localItems.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(localItems, oldIndex, newIndex);
      setLocalItems(newItems);

      // Update menu_order for all items. Pages are one request each (that's the
      // pages API); the blog row and every custom link live in site settings, so
      // they ride together in a SINGLE settings PATCH carrying the whole
      // rebuilt nav_links array (whole-list replace).
      try {
        const updates: Promise<unknown>[] = [];
        const settingsPatch: UpdateSiteSettingsRequest = {};

        newItems.forEach((item, index) => {
          if (item.type === 'blog') {
            settingsPatch.blog_menu_order = index + 1;
          } else if (item.type === 'page') {
            updates.push(
              updatePage.mutateAsync({
                id: item.id,
                data: { menu_order: index + 1 },
              })
            );
          }
        });

        // Only touch nav_links when the site actually has some.
        if (newItems.some((item) => item.type === 'link')) {
          settingsPatch.nav_links = navLinksForOrder(newItems);
        }
        if (Object.keys(settingsPatch).length > 0) {
          updates.push(updateSettings.mutateAsync(settingsPatch));
        }

        await Promise.all(updates);
        toast.success('Menu order updated');
      } catch (err) {
        toast.error('Failed to update order');
        setLocalItems(menuItems); // Revert on error
      }
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (!(await confirmDialog(`Delete "${title}"?`, { destructive: true, confirmLabel: 'Delete page' }))) return;

    if (id.startsWith(LINK_PREFIX)) {
      const index = Number(id.slice(LINK_PREFIX.length));
      const previous = localItems;
      setLocalItems((prev) => prev.filter((item) => item.id !== id));
      try {
        await updateSettings.mutateAsync({
          nav_links: navLinks.filter((_, i) => i !== index),
        });
        toast.success('Link deleted');
      } catch (err) {
        setLocalItems(previous); // Revert on error
        toast.error(err instanceof Error ? err.message : 'Failed to delete link');
      }
      return;
    }

    try {
      await deletePage.mutateAsync(id);
      setLocalItems((prev) => prev.filter((item) => item.id !== id));
      toast.success('Page deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete page');
    }
  };

  // Whole-list replace: add appends, edit swaps in place (keeping its order).
  const handleSaveLink = async (link: NavLink) => {
    const nextLinks =
      editingLinkIndex === null
        ? [...navLinks, { ...link, menu_order: displayItems.length + 1 }]
        : navLinks.map((l, i) => (i === editingLinkIndex ? { ...link, menu_order: l.menu_order } : l));
    await updateSettings.mutateAsync({ nav_links: nextLinks });
  };

  const displayItems = localItems.length > 0 ? localItems : menuItems;

  return (
    <div className="max-w-[1500px]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Pages</h1>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              setEditingLinkIndex(null);
              setLinkDialogOpen(true);
            }}
            disabled={navLinks.length >= MAX_NAV_LINKS}
            title={navLinks.length >= MAX_NAV_LINKS ? `Limit is ${MAX_NAV_LINKS} links` : undefined}
          >
            <Plus className="h-4 w-4" />
            Add link
          </Button>
          <Button asChild>
            <Link to="/sites/$siteSlug/pages/new" params={{ siteSlug }}>New Page</Link>
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-muted-foreground">Error loading pages</div>
      ) : !data?.data ? (
        <div className="text-center py-12 text-muted-foreground">
          Error: {(data as unknown as { error?: string })?.error || 'Failed to load pages'}
        </div>
      ) : (
        <>
          <p className="text-muted-foreground mb-4 text-sm">
            Drag rows to reorder the navigation menu — pages, the blog, and custom links all share one
            order. Toggle the switch to show or hide the blog; use <span className="font-medium">Add link</span>{' '}
            for a menu entry pointing at any URL.
          </p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead className="w-[150px]">Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-[140px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <SortableContext items={displayItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                    {displayItems.map((item) => (
                      <SortablePageRow
                        key={item.id}
                        item={item}
                        siteSlug={siteSlug}
                        onDelete={handleDelete}
                        isDeleting={deletePage.isPending}
                        onToggleBlogStatus={item.type === 'blog' ? handleToggleBlogStatus : undefined}
                        isUpdatingBlog={updateSettings.isPending}
                        onTogglePageNavigation={handleTogglePageNavigation}
                        onTogglePageStatus={item.type === 'page' ? handleTogglePageStatus : undefined}
                        isUpdatingPage={updatePage.isPending}
                        changed={flash.has(item.id)}
                        onEditLink={(linkItem) => {
                          setEditingLinkIndex(linkItem.linkIndex ?? null);
                          setLinkDialogOpen(true);
                        }}
                      />
                    ))}
                  </SortableContext>
                </TableBody>
              </Table>
            </div>
          </DndContext>

          {displayItems.filter((i) => i.type === 'page').length === 0 && (
            <div className="text-center py-16 mt-8 bg-muted/40 rounded-lg border">
              <p className="text-lg font-medium">No custom pages yet</p>
              <p className="text-muted-foreground mt-1 text-sm">
                Add an About, Contact, or landing page to round out your site.
              </p>
              <Button asChild className="mt-4">
                <Link to="/sites/$siteSlug/pages/new" params={{ siteSlug }}>
                  Create your first page
                </Link>
              </Button>
            </div>
          )}

          {data?.meta && data.meta.total_pages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="secondary"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <span className="px-4 py-2 text-muted-foreground">
                Page {page} of {data.meta.total_pages}
              </span>
              <Button
                variant="secondary"
                disabled={page >= data.meta.total_pages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      <NavLinkDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        link={editingLinkIndex === null ? null : navLinks[editingLinkIndex] ?? null}
        onSave={handleSaveLink}
        isSaving={updateSettings.isPending}
      />
    </div>
  );
}

// ============================================================================
// Add / Edit custom link dialog (AB#649)
// ============================================================================

function NavLinkDialog({
  open,
  onOpenChange,
  link,
  onSave,
  isSaving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  link: NavLink | null;
  onSave: (link: NavLink) => Promise<void>;
  isSaving: boolean;
}) {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [newTab, setNewTab] = useState(false);
  const [errors, setErrors] = useState<NavLinkErrors>({});

  useEffect(() => {
    if (open) {
      setLabel(link?.label ?? '');
      setUrl(link?.url ?? '');
      setNewTab(link?.new_tab ?? false);
      setErrors({});
    }
  }, [open, link]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const found = validateNavLink({ label, url });
    if (found.label || found.url) {
      setErrors(found);
      return;
    }
    try {
      await onSave({ label: label.trim(), url: url.trim(), new_tab: newTab });
      toast.success(link ? 'Link updated' : 'Link added');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save link');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{link ? 'Edit link' : 'Add link'}</DialogTitle>
            <DialogDescription>
              A navigation menu entry pointing at any URL.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nav-link-label">Label</Label>
              <Input
                id="nav-link-label"
                value={label}
                onChange={(e) => {
                  setLabel(e.target.value);
                  setErrors((prev) => ({ ...prev, label: undefined }));
                }}
                placeholder="Docs"
                aria-invalid={!!errors.label}
                autoFocus
              />
              {errors.label && <p className="text-destructive text-sm">{errors.label}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="nav-link-url">URL</Label>
              <Input
                id="nav-link-url"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setErrors((prev) => ({ ...prev, url: undefined }));
                }}
                placeholder="https://docs.wordpuppi.com"
                aria-invalid={!!errors.url}
              />
              {errors.url ? (
                <p className="text-destructive text-sm">{errors.url}</p>
              ) : (
                <p className="text-muted-foreground text-sm">
                  A full https:// URL, a path like /about, or #section.
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Switch id="nav-link-new-tab" checked={newTab} onCheckedChange={setNewTab} />
              <Label htmlFor="nav-link-new-tab">Open in new tab</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : link ? 'Save link' : 'Add link'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
